import React, { useState, useEffect, useCallback } from 'react';

import { toastr } from 'react-redux-toastr';

import View from '../common/View';

import {
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    withStyles,
    makeStyles,
    Button,
    Menu,
    MenuItem,
    Fade
} from '@material-ui/core';
import { Search, Clear } from '@material-ui/icons';
import AddCircleIcon from '@material-ui/icons/AddCircle';

import SnippetsApi from '../../collums-components/api/SnippetsApi';

import { snippetsStyles } from './styles';
import { inputSearchStyles } from '../../collums-constants/styles/stylesheets/inputSearchStyles';
import { buttonsStyles } from '../../collums-constants/styles/stylesheets/buttonsStyles';
import LoadingScreen from '../../collums-components/components/common/loadingScreen';
import Switch from '../../collums-components/components/common/customSwitch';
import ConfirmModal from '../common/ConfirmModal';
import SnippetModal from './SnippetModal';
import { useDebounce } from '../../collums-components/helpers';

const SnippetActions = ({ id, onDelete, onCopy }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const globalStyles = makeStyles(buttonsStyles)();

    return (
        <div>
            <Button
                aria-controls="fade-menu"
                aria-haspopup="true"
                className={globalStyles.grayButton}
                onClick={e => setAnchorEl(e.currentTarget)}
            >
                Options
            </Button>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                TransitionComponent={Fade}
            >
                <MenuItem
                    onClick={() => {
                        onCopy(id);
                        setAnchorEl(null);
                    }}
                >
                    Copy
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        onDelete(id);
                        setAnchorEl(null);
                    }}
                >
                    Delete
                </MenuItem>
            </Menu>
        </div>
    );
};

function SnippetsView({ classes }) {
    const [search, setSearch] = useState('');
    const [snippetsList, setSnippetsList] = useState([]);
    const [selectedSnippet, setSelectedSnippet] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmModal, setConfirmModal] = useState(false);
    const [deleteId, setDeleteId] = useState('');
    const filterDebounce = useDebounce(search, 600);

    const globalSearchStyles = makeStyles(inputSearchStyles)();
    const globalButtonsStyles = makeStyles(buttonsStyles)();

    const getSnippets = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await SnippetsApi.list(search.trim());
            setSnippetsList(res);
        } catch (err) {
            toastr.error(err?.data?.message || 'Failed to load snippets');
        }
        setIsLoading(false);
        /*eslint-disable-next-line */
    }, [filterDebounce]);

    useEffect(() => {
        getSnippets();
    }, [getSnippets]);

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await SnippetsApi.delete(deleteId);
            toastr.success('Snippet deleted');
            getSnippets();
        } catch (err) {
            toastr.error(err?.data?.message || 'Failed to delete snippet');
        }
    };

    const handleCopy = async id => {
        try {
            await SnippetsApi.copy(id);
            toastr.success('Snippet copied');
            getSnippets();
        } catch (err) {
            toastr.error(err?.data?.message || 'Failed to copy snippet');
        }
    };

    const changeActive = async snippet => {
        if (!snippet?.id) return;
        try {
            await SnippetsApi.changeActive(snippet.id);
            getSnippets();
            toastr.success(`Snippet "${snippet.name}" updated`);
        } catch (err) {
            toastr.error(err?.data?.message || 'Something went wrong');
        }
    };

    const getPractitionersLabel = snippet => {
        if (!snippet.practitioners || snippet.practitioners.length === 0) return '';
        if (snippet.allPractitioners) return 'All practitioners';
        return snippet.practitioners
            .map(p => p.displayName || (p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : p.firstName || ''))
            .join(', ');
    };

    return (
        <>
            <View>
                <div className={classes.header}>
                    <TextField
                        className={globalSearchStyles.inputSearch}
                        variant="outlined"
                        value={search}
                        onKeyPress={e => {
                            if (e.key === 'Enter') getSnippets();
                        }}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <>
                                    <IconButton
                                        className={globalSearchStyles.icons}
                                        onClick={() => setSearch('')}
                                        disabled={!search}
                                    >
                                        <Clear style={!search ? { color: 'transparent' } : {}} />
                                    </IconButton>
                                    <IconButton
                                        position="end"
                                        onClick={getSnippets}
                                        className={globalSearchStyles.icons}
                                    >
                                        <Search />
                                    </IconButton>
                                </>
                            )
                        }}
                    />
                    <IconButton onClick={() => setIsModalVisible(true)}>
                        <AddCircleIcon className={globalButtonsStyles.addCircleStyle} />
                    </IconButton>
                </div>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography>Active</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography>Name</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography>Practitioners</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography></Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {snippetsList.map(snippet => (
                            <TableRow key={snippet.id}>
                                <TableCell>
                                    <Switch checked={snippet.active} onChange={() => changeActive(snippet)} />
                                </TableCell>
                                <TableCell>
                                    <Typography>
                                        <Link
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                setSelectedSnippet(snippet);
                                                setIsModalVisible(true);
                                            }}
                                        >
                                            {snippet.name}
                                        </Link>
                                    </Typography>
                                </TableCell>
                                <TableCell className={classes.practitionersCell}>
                                    <Typography>{getPractitionersLabel(snippet)}</Typography>
                                </TableCell>
                                <TableCell>
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <SnippetActions
                                            id={snippet.id}
                                            onDelete={id => {
                                                setDeleteId(id);
                                                setConfirmModal(true);
                                            }}
                                            onCopy={handleCopy}
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {isModalVisible && (
                    <SnippetModal
                        snippet={selectedSnippet}
                        isOpen={isModalVisible}
                        onClose={() => {
                            setSelectedSnippet(null);
                            setIsModalVisible(false);
                            getSnippets();
                        }}
                    />
                )}
                {confirmModal && (
                    <ConfirmModal
                        isOpen
                        setIsOpen={setConfirmModal}
                        onConfirm={handleDelete}
                        title="Delete snippet"
                        content="Are you sure you want to delete this snippet?"
                    />
                )}
                {isLoading && <LoadingScreen />}
            </View>
        </>
    );
}

export default withStyles(snippetsStyles)(SnippetsView);
